import React from 'react';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import ErrorOutlineRoundedIcon from '@mui/icons-material/ErrorOutlineRounded';
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded';
import SyncRoundedIcon from '@mui/icons-material/SyncRounded';
import { useTranslation } from 'react-i18next';
import { AlertBanner } from '@bloomays-lib/ui.shared';
import type { AlertBannerTone } from '@bloomays-lib/ui.shared';
import type { MissionAlert, ContactAlarm, ContactSlotKey } from '../../../helpers/missionDashboardV2';

/** Bandeau d'alertes documentaires (`.alert.warn` / `.alert.danger`). */
export const AlertsBanner = ({
  alerts,
  onRegularize,
}: {
  alerts: MissionAlert[];
  onRegularize?: (kind: string) => void;
}) => {
  const { t } = useTranslation(['missionDashboard']);
  if (alerts.length === 0) return null;
  return (
    <Stack spacing="8px" sx={{ marginBottom: '14px' }}>
      {alerts.map((alert) => {
        // Tons (retour design) : manquant / signature urgente (mission démarrée) → rouge (prioritaire) ;
        // expiré → jaune ; signature en cours → bleu.
        const tone: AlertBannerTone =
          alert.status === 'missing' || (alert.status === 'signing' && alert.urgent)
            ? 'danger'
            : alert.status === 'signing'
            ? 'info'
            : 'warning';
        const icon =
          tone === 'danger' ? (
            <ErrorOutlineRoundedIcon sx={{ fontSize: 16 }} />
          ) : tone === 'info' ? (
            <SyncRoundedIcon sx={{ fontSize: 16 }} />
          ) : (
            <WarningAmberRoundedIcon sx={{ fontSize: 16 }} />
          );
        // `alert.name` provient des helpers purs (testés) — on n'externalise QUE le suffixe construit en vue.
        const message =
          alert.status === 'missing'
            ? `${alert.name} — ${
                alert.kind === 'contract'
                  ? t('alerts.contractMissing', { ns: 'missionDashboard' })
                  : t('alerts.documentNotUploaded', { ns: 'missionDashboard' })
              }`
            : alert.status === 'signing'
            ? `${alert.name} — ${t('alerts.signing', { ns: 'missionDashboard' })}${
                alert.urgent ? t('alerts.missionStarted', { ns: 'missionDashboard' }) : ''
              }`
            : `${alert.name} — ${t('alerts.expired', { ns: 'missionDashboard' })}${
                alert.expirationDate
                  ? t('alerts.expiredOn', {
                      ns: 'missionDashboard',
                      date: new Date(alert.expirationDate).toLocaleDateString('fr-FR'),
                    })
                  : ''
              }`;
        return (
          <AlertBanner
            key={`${alert.kind}-${alert.name}-${alert.status}`}
            tone={tone}
            icon={icon}
            onClick={onRegularize ? () => onRegularize(alert.kind) : undefined}
            action={
              onRegularize ? (
                <Box
                  component="span"
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '3px',
                    fontWeight: 600,
                    whiteSpace: 'nowrap',
                  }}
                >
                  {t('alerts.regularize', { ns: 'missionDashboard' })} <ChevronRightRoundedIcon sx={{ fontSize: 15 }} />
                </Box>
              ) : null
            }
          >
            {message}
          </AlertBanner>
        );
      })}
    </Stack>
  );
};

/**
 * Bandeau d'alarmes de non-conformité des contacts (§8, décision spec #4) : NON bloquant — signale
 * les rôles obligatoires (min 1) sans aucun contact, avec un raccourci « Ajouter ». Vide si conforme.
 */
export const ContactAlarmsBanner = ({
  alarms,
  onFix,
}: {
  alarms: ContactAlarm[];
  onFix?: (key: ContactSlotKey) => void;
}) => {
  const { t } = useTranslation(['missionDashboard']);
  // Libellé de côté (vue) ; `alarm.label` provient du helper pur (testé).
  const sideLabel: Record<'client' | 'supplier', string> = {
    client: t('contactAlarms.sideClient', { ns: 'missionDashboard' }),
    supplier: t('contactAlarms.sideSupplier', { ns: 'missionDashboard' }),
  };
  if (alarms.length === 0) return null;
  return (
    <Stack spacing="8px" sx={{ marginBottom: '12px' }}>
      {alarms.map((alarm) => (
        <AlertBanner
          key={alarm.key}
          tone="warning"
          icon={<WarningAmberRoundedIcon sx={{ fontSize: 16 }} />}
          action={
            onFix ? (
              <Box
                component="button"
                type="button"
                onClick={() => onFix(alarm.key)}
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '3px',
                  fontWeight: 600,
                  whiteSpace: 'nowrap',
                  cursor: 'pointer',
                  border: 'none',
                  background: 'transparent',
                  color: 'inherit',
                  fontSize: 12,
                }}
              >
                {t('contactAlarms.add', { ns: 'missionDashboard' })} <ChevronRightRoundedIcon sx={{ fontSize: 15 }} />
              </Box>
            ) : null
          }
        >
          {sideLabel[alarm.side]} · {alarm.label} — {t('contactAlarms.noContactRequired', { ns: 'missionDashboard' })}
        </AlertBanner>
      ))}
    </Stack>
  );
};
